import Vue from "vue";
import { TVOptionSetManagerInput } from "~/interfaces/vSelectInterfaces"
import VOptionSet from "~/models/vOptionSet/vOptionSet";
import VOptionSetManager from "~/models/vOptionSet/vOptionSetManager";
import { teleport } from '~/modules/nodeManager';
import _ from "lodash";

export default Vue.extend({
  inheritAttrs: false,
  components: {
    Avatar: () => import('~/components/avatar.vue'),
  },
  props: {
    isSelectable: {
      type: Boolean,
      default: true
    },
    tooltip: {
      type: [String, null],
      default: null
    },
    options: {
      type: [
        Array as () => TVOptionSetManagerInput[],
        Object as () => TVOptionSetManagerInput
      ],
      default: () => {
        return [
          {
            options: [],
            endpoint: undefined,
            valueKey: 'value',
            labelKey: 'label',
          }
        ];
      },
      required: true
    },
    value: {},
    isSearchable: {
      type: Boolean,
      default: true
    },
    placeholder: {
      type: String,
      default: 'Select one...'
    },
    bindByObject: {
      type: Boolean,
      default: false
    },
    bindByArray: {
      type: Boolean,
      default: false,
    },
    multiselect: {
      type: Boolean,
      default: false
    },
    /**
     * Will likely need this array for VOptionSetManager to dependably keep track of selected options
     */
    selectedOptionIds: {
      type: Array,
      default: () => []
    },
    /**
     * When in multiselect mode, show label/checkboxes instead of buttons
     */
    showCheckboxes: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false
    },
    /**
     * options show but are not clickable
     */
    readonly: {
      type: Boolean,
      default: false
    },
    barClasses: {
      type: [
        String,
        Array as () => string[]
      ],
      default: null
    },
    hideResults: {
      type: Boolean,
      default: false
    },
    autoFocus: {
      type: Boolean,
      default: false,
    },
    forceNullifyIfNull: {
      type: Boolean,
      default: false,
    },
    autocomplete: {
      type: String,
      default: "on"
    },
    hasTeleport: {
      type: Boolean,
      default: false
    },
    compress: {
      type: Boolean,
      default: false
    },
    btnArrow: {
      type: Boolean,
      default: true
    },
    btnColor: {
      type: String,
      default: 'input-color'
    },
    btnIcon: {
      type: String,
      default: ''
    },
    btnSize: {
      type: String,
      default: ''
    },
    alwaysShowSearchBox: {
      type: Boolean,
      default: false,
    },
    /**
     * Allow a-select to remain open after a selection is made
     */
    stayOpen: {
      type: Boolean,
      default: false,
    },
    showCreateApp: {
      type: Boolean,
      default: false
    },
    ariaInput: {
      type: Object,
      default: () => {
        return {}
      },
    },
    missingOptionErrorMessage: {
      type: String,
      default: null,
    },
    debounce: {
      type: Boolean,
      default: true
    },
  },
  data() {
    return {
      isOpen: !this.isSelectable as boolean,
      q: '' as string,
      vOptionSetManager: new VOptionSetManager(this.options, this.multiselect, this.selectedOptionIds),
      teleport: teleport,
      searchCount: 0,
      preventBlurOnce: false,
    }
  },
  mounted() {
    this.vOptionSetManager.initializeSelectedOption(this.value);

    this.vOptionSetManager.sets.forEach((set) => {
      set.valueKey ? set.valueKey.toString() : set.valueKey;
      set.altKey = set.altKey === 'value' ? set.valueKey : set.altKey;
    });
  },
  computed: {
    selectedLabel: {
      get(): string {
        if (this.multiselect) {
          return this.placeholder;
        } else if (this.vOptionSetManager.lastSelectedOption && Object.keys(this.vOptionSetManager.lastSelectedOption).length > 0) {
          return this.vOptionSetManager.selectedLabel || this.placeholder;
        }
        return this.placeholder;
      }
    },
    selectedLabelSuffix(): string {
      if (this.multiselect) {
        return "";
      } else if (this.vOptionSetManager.lastSelectedOption) {
        return this.vOptionSetManager.selectedLabelSuffix ? this.vOptionSetManager.selectedLabelSuffix : "";
      } else {
        return "";
      }
    },
    selectedIcon(): string {
      if (this.multiselect) {
        return "";
      } else if (this.vOptionSetManager.lastSelectedOption) {
        return this.vOptionSetManager.selectedIcon || this.vOptionSetManager.lastSelectedOption?.icon || "";
      } else {
        return "";
      }
    },
    selectedValue: {
      get(): string {
        return this.vOptionSetManager.lastSelectedOption ? this.vOptionSetManager.lastSelectedOption.value : this.placeholder;
      }
    },
    selectedPlaceholder(): string {
      return this.vOptionSetManager.selectedPlaceholder
    },
    searchPlaceholder(): string {
      if (this.btnIcon !== '' || this.alwaysShowSearchBox) {
        return this.selectedPlaceholder || this.placeholder;
      } else {
        return this.selectedLabel;
      }
    },
    classesAselect() {
      return [
        {
          "open": this.isOpen,
          "compress": this.compress
        }
      ]
    },
    classesBtn: {
      get(): object {
        const baseClass = [
          this.btnIcon !== '' ? 'btn-icon' : 'btn',
          this.btnSize,
          this.btnColor, {
            "arrow": this.btnArrow && this.btnIcon === '',
            "compress": this.compress,
            "disabled": this.disabled,
          },
        ]
        if (this.vOptionSetManager.lastSelectedOption?.btnClass) {
          Object.assign(baseClass, {[this.vOptionSetManager.lastSelectedOption.btnClass]: true});
        }
        return baseClass;
      }
    },
    classesRow() {
      return this.barClasses
    },
    shouldShowSearchBox: {
      get(): boolean {
        return this.isSearchable && !this.disabled && (this.multiselect || !this.isSelectable || this.isOpen || this.alwaysShowSearchBox);
      }
    },
    hasQ: {
      get(): boolean {
        return this.q.length > 0;
      }
    },
    hasOptions: {
      get(): boolean {
        return this.vOptionSetManager.hasOptions;
      }
    },
    hasPinnedOptions: {
      get(): boolean {
        return this.vOptionSetManager.hasPinnedOptions;
      }
    },
    debouncedHandleQChange(): Function {
      return _.debounce((newVal) => this.handleQChange(newVal), window.AppConfig.queryTimeout());
    },
  },
  methods: {
    open(): void {
      this.isOpen = true;
    },
    show(event): void {
      if (this.disabled) return;
      this.vOptionSetManager.highlightFirst();
      if (this.isOpen === false) {
        this.open();
      }
      if (this.vOptionSetManager.lastSelectedOption === null) {
        this.vOptionSetManager.initializeSelectedOption(this.value);
      }

      if(this.hasTeleport) {
        this.teleport.aselect(event);
      }

      this.focus(event);
    },
    focus(event) {
      if (this.shouldShowSearchBox) {
        Vue.nextTick(() => this.focusSearch());
      } else {
        Vue.nextTick(() => this.focusDropdown());
      }
      this.$emit('focus', event)
    },
    focusSearch() {
      $(this.$refs.searchInput.$el).find(':input').get(0).focus();
    },
    focusDropdown() {
      this.$refs.dropdown.focus();
    },
    focusActive(event) {
      this.$nextTick(() => $(this.$refs.aSelect).find('.active').focus())
    },
    forceBlur(event): void {
      // using event.relatedTarget to detect whether it was a click on a
      // button doesn't work on some browsers
      this.hide();
      this.killTeleport();
      this.preventBlurOnce = null
      this.$emit('blur', event);
    },
    blur(event): void {
      if (this.preventBlurOnce) {
        this.preventBlurOnce = false
        return
      }
      // do not blur if configured to stay open and click is inside the a-select
      if (this.stayOpen && $(this.$el).has(event.relatedTarget).length) return
      this.forceBlur(event)
    },
    blurIfNotSearchable(event): void {
      if (!this.isSearchable) {
        this.blur(event);
      }
    },
    killTeleport() {
      if(this.hasTeleport && $('#t-aselect').length > 0){
        this.teleport.reset();
      }
    },
    hide(): void {
      this.isOpen = false;
      this.vOptionSetManager.setHighlightedIndex();
    },
    // listening on `mousedown` as the blur firing cancels the `click` on some browsers
    // listening on `click` handles keyboard's 'Enter' and maybe keeps this more accessible
    handleOptionSelect(event) {
      this.setValue(event)
    },
    handleOptionSelectCapture(event) {
      // if stayOpen, then the input selection is handled by `handleOptionSelect` on the mousedown
      if (!this.stayOpen) {
        this.setValue(event)
      }
    },
    setValue(event) {
      if (!this.vOptionSetManager.hasIndex || !this.vOptionSetManager.hasOptions) return;

      let emittedValue = this.vOptionSetManager.setLastSelectedFromHighlight();

      if (this.bindByArray) {
        emittedValue = this.vOptionSetManager.selectedOptionIds;
      } else if (!this.bindByObject) {
        emittedValue = this.vOptionSetManager.selectedValue;
      }

      if (emittedValue === undefined || emittedValue === null) {
        // TODO: this can't handle when value desired IS undefined or null
        // do nothing if nothing found
      } else {
        if (this.vOptionSetManager.multiselect && this.vOptionSetManager.isSelected(this.vOptionSetManager.lastSelectedOption)) {
          const lastSelectedOption = this.vOptionSetManager.lastSelectedOption;
          this.vOptionSetManager.unsetLastSelected();

          if (this.bindByArray) {
            this.$emit('input', emittedValue, event);
            this.$emit('deselected', lastSelectedOption, event);
          }
          this.$emit('destroy', emittedValue, event);
        } else {
          this.vOptionSetManager.confirmLastSelected();
          this.$emit('input', emittedValue, event);
          if (this.bindByArray) this.$emit('selected', this.vOptionSetManager.lastSelectedOption, event);
        }
        if (!this.stayOpen) {
          this.resetSelect();
        }
      }
    },
    resetSelect(): void {
      this.hide();
      this.q = '';
      if (this.multiselect && this.isSearchable) {
        this.focusSearch();
      } else {
        this.$nextTick(() => this.focusDropdown());
      }
      this.killTeleport();
    },
    isHighlighted(setIndex, optionIndex): boolean {
      return this.vOptionSetManager.isHighlighted(setIndex, optionIndex);
    },
    isSelected(option): boolean {
      return this.vOptionSetManager.isSelected(option);
    },
    highlightNext(): void {
      this.vOptionSetManager.highlightNext();
    },
    highlightPrevious(): void {
      this.vOptionSetManager.highlightPrevious();
    },
    handleKeyDown: function (event) {
      let handledKeys = ['ArrowUp', 'ArrowDown', 'Enter', 'Escape'];
      if (handledKeys.includes(event.key)) {
        event.preventDefault();
        event.stopPropagation();
        switch (event.key) {
          case 'ArrowUp':
            if (this.vOptionSetManager.hasIndex) {
              this.vOptionSetManager.highlightPrevious();
              this.preventBlurOnce = true
              this.focusActive(event)
            }
            break;
          case 'ArrowDown':
            if (this.vOptionSetManager.hasIndex) {
              this.vOptionSetManager.highlightNext();
              this.preventBlurOnce = true
              this.focusActive(event)
            }
            break;
          case 'Enter':
            if (this.isOpen) {
              this.setValue(event);
            } else {
              this.show();
            }
            break;
          case 'Escape':
            this.hide();
            this.killTeleport();
            break;
        }
        return false;
      }
    },
    // see comment around .handleOptionSelect() on why this needs
    // to listen on mousedown also
    emitAction(event): void {
      let emittedValue = this.vOptionSetManager.setLastSelectedFromHighlight();
      this.$emit('action', emittedValue);
      this.resetSelect();
    },
    onMouseleave(event): void {
      if (!this.isOpen) {
        this.$emit('mouseleave', event);
      }
    },
    handleOnFocus(event): void {
      if (this.hasQ) {
        this.open();
      }
    },
    handleQChange(newVal: string): void {
      if (newVal) {
        this.open();
        window.CapybaraLockstep?.startWork('a-select search')
        this.searchCount += 1;
        this.vOptionSetManager.search(newVal).then((response) => {
          this.$emit('search:finished', response);
          this.$forceUpdate();
          for (let index = 0; index < this.searchCount; index++) {
            window.CapybaraLockstep?.stopWork('a-select search')
          }
          this.searchCount = 0;
        });
      } else {
        this.vOptionSetManager.resetOptions();
        this.$emit('search:finished');
        this.$forceUpdate(); // so all options show when query string is empty
      }
    },
    selectedColor(set: VOptionSet): string {
      return set.selectedColor?.toLowerCase();
    },
    createApp() {
      this.$emit('createApp');
    }
  },
  watch: {
    q(newVal): void {
      this.debounce ? this.debouncedHandleQChange(newVal) : this.handleQChange(newVal)
    },
    value(newVal) {
      if (this.forceNullifyIfNull && !newVal) {
        // not the best solution - need to update selectedOption somehow so undo redo object model property linked relation property client id displays correctly
        this.vOptionSetManager.nullifyLastSelected();
      }
      this.vOptionSetManager.initializeSelectedOption(this.value);
    },
    selectedOptionIds(newVal): void {
      this.vOptionSetManager.selectedOptionIds = newVal;
    },
    options(newVal): void {
      if (!this.isOpen) {
        this.vOptionSetManager.load(newVal, this.multiselect, this.selectedOptionIds);
        this.vOptionSetManager.initializeSelectedOption(this.value);
      }
    }
  },
});